import { useQuery, useQueries } from "@tanstack/react-query";
import axios from "axios";
import { getItems } from "./items";
import { createRecord } from "./useCreateRecord";

export type Holiday = {
  date: string;
  name: string;
  type: "national" | "local";
  location?: number | string;
};

// Clinic ids for the `location` relation on portugal_holidays. Local holidays must store the clinic id so
// the per-clinic match in buildClinicHolidaySet works (the column is an integer FK to clinics).
const PORTO_CLINIC_ID = 1;
const BRAGA_CLINIC_ID = 3;

type NewHolidayRecord = {
  entity: string,
  data: Array<Holiday>
}

export const usePortugalHolidays = (date: Date, year?: number) => {
  const queryYear = year ?? date.getFullYear();
  return useQuery({
    queryKey: ["portugal-holidays", queryYear],
    queryFn: () => getPortugalHolidays(queryYear),
  });
};

export const usePortugalHolidaysRange = (startYear: number, endYear: number) => {
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => startYear + i);

  const results = useQueries({
    queries: years.map((year) => ({
      queryKey: ["portugal-holidays", year],
      queryFn: () => getPortugalHolidays(year),
    })),
  });

  const isLoading = results.some((r) => r.isLoading);
  const allHolidays = results.flatMap((r) => r.data || []);

  return { data: allHolidays, isLoading };
};

export const getPortugalHolidays = async (year: number): Promise<Array<Holiday>> => {
    const entity = "portugal_holidays"

    try {
        const responseBE = await getPortugalHolidaysFromBE(year, entity);
        if (responseBE.length > 0 ) return responseBE

        const holidays = await getPortugalHolidaysFromAPI(year);
        if (holidays.length === 0) return [];

        const newRecord: NewHolidayRecord = {
            entity: entity,
            data: holidays
        };
        const responseCreate = await createPortugalHolidaysOnBE(newRecord);
        return responseCreate;
        
    } catch (error) {
        console.error("Failure getting Holidays from both BE and API:", error);
        return []
    }
};


const getPortugalHolidaysFromAPI = async (year: number): Promise<Array<Holiday>> => {
  try {
    // Fetch national holidays from Nager.Date
    const response = await axios.get(
      `https://calendarific.com/api/v2/holidays?api_key=da34YuKJJM2OUTw8CRcptz15NE1VVKO0&country=pt&year=${year}&type=national`
    );
    const nationalHolidays = response.data.response.holidays;

    const holidays: Array<Holiday> = nationalHolidays.map((holiday: any) => ({
      date: holiday.date.iso,
      name: holiday.name,
      type: "national",
    }));

    // Add local holidays manually
    const localHolidays: Array<Holiday> = [
      {
        date: `${year}-06-24`,
        name: "São João",
        type: "local",
        location: PORTO_CLINIC_ID,
      },
      {
        date: `${year}-06-24`,
        name: "São João",
        type: "local",
        location: BRAGA_CLINIC_ID,
      },
    ];

    return [...holidays, ...localHolidays];
  } catch (error) {
    console.error("Failed to fetch holidays from API:", error);
    return [];
  }
}

const getPortugalHolidaysFromBE = async (year: number, entity: string): Promise<Array<Holiday>> => {
    const response = await getItems(entity, "*", {
      filter: {
        date: {
          _gte: `${year}-01-01T00:00:00`,
          _lt: `${year+1}-01-01T00:00:00`
        }
      }
    })
    return response.items
    
}

const createPortugalHolidaysOnBE = async (record: NewHolidayRecord) => {
  const response =  await createRecord(record)
  return response
}